import { authApi } from "../services/auth.api";
import StorageService from "../services/storage.service";
import { removeUser, setUser } from "../store/authSlice";
import { useTypedDispatch } from "./useTypedDispatch";
import { z } from "zod";
import { getErrMsg } from "../messages/errorMessages";
import { IOauthState } from "../pages/Oauth.page";
import checkFormStateByZod from "../utils/checkFormStateByZod";
import generateRecaptchaV3 from "../utils/generateRecaptchaV3";
import toast from "react-hot-toast";
import { getErrorFromCatch, serializeFieldErrors } from "../services/api";
import { IUser } from "../../model/IUser";

// --
// -- Zod validation scheme
// --
// -- SIGN IN validation scheme
const signInScheme = z.object({
  email: z
    .string()
    .email(getErrMsg("wrong-email"))
    .min(1, getErrMsg("empty-email")),
  password: z.string().min(1, getErrMsg("empty-password")),
} as Record<keyof IOauthState["signIn"], any>);

// -- SIGN UP validation scheme
const signUpScheme = z
  .object({
    email: z
      .string()
      .email(getErrMsg("wrong-email"))
      .min(1, getErrMsg("empty-email")),
    password: z
      .string()
      .min(1, getErrMsg("short-password"))
      .min(5, getErrMsg("empty-password")),
    passwordConfirm: z.string().min(1, getErrMsg("empty-password-confirm")),
  } as Record<Partial<keyof IOauthState["signUp"]>, any>)
  .superRefine(({ passwordConfirm, password }, ctx) => {
    if (passwordConfirm !== password) {
      ctx.addIssue({
        code: "custom",
        message: getErrMsg("no-match-passwords"),
        path: ["passwordConfirm"],
      });
    }
  });

// -- ACTIVATION validation scheme
const activationScheme = z.object({
  activationKey: z.string().min(1, getErrMsg("empty-activation-key")),
} as Record<keyof IOauthState["activate"], any>);

export function useAuth() {
  const dispatch = useTypedDispatch();

  const [trySignIn] = authApi.useSignInMutation();
  const [tryActivate] = authApi.useActivateMutation();
  const [trySignUp] = authApi.useSignUpMutation();
  const [getMyself] = authApi.useLazyMeQuery();

  // -- Get User using API
  const setMyself = async (user?: IUser) => {
    const { data } = await getMyself({});
    return data ? dispatch(setUser(data)) : dispatch(removeUser());
  };

  const unSetMyself = () => {
    dispatch(removeUser());
    storeTokens();
  };

  // --
  // -- return: null - OK, { error? } - Error
  // --
  const handleSignUp = async ({
    signUp,
  }: IOauthState): Promise<null | Partial<IOauthState["signUp"]>> => {
    // -- Client validation form
    const clientErrs = checkFormStateByZod(signUpScheme, signUp);
    if (clientErrs) return clientErrs;

    // -- Generate Recaptcha
    const tokenRecaptcha = await generateRecaptchaV3(
      process.env.REACT_APP_SIGNUP_ACTION
    );
    if (!tokenRecaptcha) {
      toast.error(`${getErrMsg("empty-recaptcha")}`, { duration: 8000 });
      return {};
    }

    try {
      const data = await trySignUp({ tokenRecaptcha, ...signUp }).unwrap();
      await setMyself();
      return null;
    } catch (err) {
      toast.error(`${getErrMsg("signup-failed")} ${getErrorFromCatch(err)}`, {
        duration: 8000,
      });
      return serializeFieldErrors(err) ?? { email: getErrorFromCatch(err) };
    }
  };

  // --
  // -- return: null - OK, { error? } - Error
  // --
  const handleActivate = async ({
    activate,
  }: IOauthState): Promise<null | Partial<IOauthState["activate"]>> => {
    // -- Client validation form
    const clientErrs = checkFormStateByZod(activationScheme, activate);
    if (clientErrs) return clientErrs;

    // -- Generate Recaptcha
    const tokenRecaptcha = await generateRecaptchaV3(
      process.env.REACT_APP_SIGNUP_ACTION
    );
    if (!tokenRecaptcha) {
      toast.error(`${getErrMsg("empty-recaptcha")}`, { duration: 8000 });
      return {};
    }

    try {
      const data = await tryActivate({ tokenRecaptcha, ...activate }).unwrap();
      return null;
    } catch (err) {
      toast.error(
        `${getErrMsg("activation-failed")} ${getErrorFromCatch(err)}`,
        { duration: 8000 }
      );
      return serializeFieldErrors(err) ?? { email: getErrorFromCatch(err) };
    }
  };

  const handleSignIn = async ({
    signIn,
  }: IOauthState): Promise<null | Partial<IOauthState["signIn"]>> => {
    // -- Client validation form
    const clientErrs = checkFormStateByZod(signInScheme, signIn);
    if (clientErrs) return clientErrs;

    // -- Generate Recaptcha
    const tokenRecaptcha = await generateRecaptchaV3(
      process.env.REACT_APP_SIGNUP_ACTION
    );
    if (!tokenRecaptcha) {
      toast.error(`${getErrMsg("empty-recaptcha")}`, { duration: 8000 });
      return {};
    }

    try {
      const data = await trySignIn({ tokenRecaptcha, ...signIn }).unwrap();
      if (!data?.accessToken || !data?.refreshToken) throw new Error();
      storeTokens(data.accessToken, data.refreshToken);
      await setMyself();
      return null;
    } catch (err) {
      toast.error(`${getErrMsg("signin-failed")} ${getErrorFromCatch(err)}`, {
        duration: 8000,
      });
      return serializeFieldErrors(err) ?? { email: getErrorFromCatch(err) };
    }
  };

  const handleLogout = () => {
    unSetMyself();
  };

  const storeTokens = (access = "", refresh = ""): void => {
    StorageService.setToken("access", access);
    StorageService.setToken("refresh", refresh);
  };

  return {
    setMyself,
    unSetMyself,

    handleSignIn,
    handleSignUp,
    handleLogout,
    handleActivate,
  };
}
